export interface DashboardScrollEntry {
  top: number;
  updatedAt: number;
}

type DashboardScrollStore = Record<string, DashboardScrollEntry>;

const STORAGE_KEY = 'wl_dashboard_scroll_positions';
const MAX_ENTRIES = 80;

export function buildDashboardScrollKey(pathname: string, search: string) {
  return `${pathname}${search}`;
}

export function readDashboardScrollPosition(
  storage: Pick<Storage, 'getItem'>,
  key: string,
): number | null {
  const store = readStore(storage);
  const entry = store[key];
  return entry && Number.isFinite(entry.top) && entry.top >= 0 ? entry.top : null;
}

export function writeDashboardScrollPosition(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  key: string,
  top: number,
  updatedAt = Date.now(),
) {
  if (!Number.isFinite(top) || top < 0) return;

  const entries = Object.entries({
    ...readStore(storage),
    [key]: { top, updatedAt },
  }).sort(([, left], [, right]) => right.updatedAt - left.updatedAt);

  storage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries.slice(0, MAX_ENTRIES))));
}

function readStore(storage: Pick<Storage, 'getItem'>): DashboardScrollStore {
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}') as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
        const entry = value as Partial<DashboardScrollEntry>;
        return Number.isFinite(entry.top) && Number.isFinite(entry.updatedAt);
      }),
    ) as DashboardScrollStore;
  } catch {
    return {};
  }
}
